import { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import WasteToolbarThumbnail from "../imageCard/WasteToolbarThumbnail";
import { LoadingScreen } from "../loader/LoadingScreen";
import { getSizeRange } from "../../shared/utils/sizeRangeUtils";
import { formattedColorMaterial } from "../formatted/FormattedText";
import { getParentCategoryLabelPlural } from "../../containers/characterization-2/core/utils/utils";
import { capitalizeFirstLetter } from "./Date";
import { countCategoryPropagationImpact } from "../../services/container";

const WastePolygonModal = ({
  setIsModalView,
  isModalView,
  selectedAnomalie,
  imageUrl,
  captureTime,
  containerId,
  handleObjectSegmentation,
  handleDeleteSegmentation,
  handleDeleteSuggestion,
  onRestore,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [requestType, setRequestType] = useState("category"); // "category" | "delete"
  const [loading, setLoading] = useState(false);
  const [showSegmentation, setShowSegmentation] = useState(true);
  const [impactCount, setImpactCount] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [propagationProgress, setPropagationProgress] = useState(null);
  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );
  const viewMode = Cookies.get("viewMode") || "client";

  const wasteCategoriesByParent = useMemo(() => {
    const raw = localStorage.getItem("materialsDescription");
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      const byParent = {};
      (parsed || []).forEach((item) => {
        const ref = item?.referenceName;
        if (!ref || String(ref).toLowerCase().startsWith("hw_")) return;

        const parentKey = t(item.parentCategory) || t("other");
        const key = String(parentKey).toLowerCase();
        if (!byParent[key]) byParent[key] = [];
        byParent[key].push({ referenceName: ref });
      });
      const sortRefs = (a, b) =>
        (a.referenceName || "").localeCompare(
          b.referenceName || "",
          undefined,
          {
            sensitivity: "base",
          },
        );
      Object.keys(byParent).forEach((k) => byParent[k].sort(sortRefs));
      const groups = Object.entries(byParent).map(
        ([parentCategory, items]) => ({
          parentCategory,
          items,
        }),
      );
      const otherLabel = t("other");
      groups.sort((a, b) => {
        const labelA = a.parentCategory || "";
        const labelB = b.parentCategory || "";
        if (labelA.toLowerCase() === otherLabel.toLowerCase()) return 1;
        if (labelB.toLowerCase() === otherLabel.toLowerCase()) return -1;
        return labelA.localeCompare(labelB, undefined, {
          sensitivity: "base",
        });
      });
      return groups;
    } catch (e) {
      console.error("Failed to parse materialsDescription:", e);
      return [];
    }
  }, [t]);

  useEffect(() => {
    setComment(selectedAnomalie?.comment || "");
    setSelectedCategory(
      selectedAnomalie?.reviewCls || selectedAnomalie?.cls || "",
    );
    const isDeletion =
      selectedAnomalie?.reviewRequestType === "delete" ||
      selectedAnomalie?.suggestedForDeletion ||
      !!selectedAnomalie?.deleteSuggestionComment?.trim();
    setRequestType(isDeletion ? "delete" : "category");
    setImpactCount(null);
    setPropagationProgress(null);
    // Disable Valider until impact count resolves for category requests.
    setImpactLoading(!isDeletion);
  }, [selectedAnomalie, isModalView]);

  // Async impact count — does not block modal render
  useEffect(() => {
    if (!isModalView || !selectedAnomalie || !containerId || !captureTime) {
      return undefined;
    }
    if (requestType !== "category") {
      setImpactCount(null);
      return undefined;
    }

    let cancelled = false;
    setImpactLoading(true);

    countCategoryPropagationImpact({
      containerId,
      captureTime,
      sourceObject: selectedAnomalie,
      trackObjectId: selectedAnomalie.trackObjectId,
      firstAppearanceAt: selectedAnomalie.firstAppearanceAt,
    })
      .then((res) => {
        if (cancelled) return;
        const count = res?.data?.impactedCount;
        setImpactCount(typeof count === "number" ? count : 1);
      })
      .catch((err) => {
        console.warn("category propagation impact count failed:", err);
        if (!cancelled) setImpactCount(1);
      })
      .finally(() => {
        if (!cancelled) setImpactLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isModalView, selectedAnomalie, containerId, captureTime, requestType]);

  const toggle = () => {
    setComment("");
    setSelectedCategory("");
    setIsModalView(!isModalView);
  };

  const sizeRangeLabel = useMemo(() => {
    const longestSideMeters = selectedAnomalie?.longestSideMeters;
    if (longestSideMeters === undefined || longestSideMeters === null)
      return "";
    const range = getSizeRange(longestSideMeters);
    if (!range || range === "total") return "";
    const formatted = range.replace("-", " à ");
    return `(${formatted}m)`;
  }, [selectedAnomalie]);

  const materialName = selectedAnomalie?.oldCls
    ? t(selectedAnomalie.oldCls)
    : t(selectedAnomalie?.cls || "");

  const confirmPropagationIfNeeded = () => {
    if (requestType !== "category") return true;
    if (impactLoading) {
      return window.confirm(t("categoryPropagation.confirmWhileLoading"));
    }
    if (typeof impactCount === "number" && impactCount > 1) {
      return window.confirm(
        t("categoryPropagation.confirmMultiImage", {
          imageCount: impactCount,
        }),
      );
    }
    return true;
  };

  return (
    <Modal
      isOpen={isModalView}
      toggle={toggle}
      contentClassName="rounded-lg"
      style={{ maxWidth: "600px" }}
      centered
    >
      <ModalHeader className="px-6 flex items-center justify-between pb-1 border-0">
        <div className="flex items-baseline gap-2">
          <span className="text-gray-900 font-semibold text-lg">
            {t(materialName)}
          </span>
          {sizeRangeLabel && (
            <span className="ml-2 text-base font-medium text-gray-500">
              {sizeRangeLabel}
            </span>
          )}
        </div>
        <CloseIcon
          style={{
            color: "#6B7280",
            cursor: "pointer",
            fontSize: "20px",
          }}
          onClick={toggle}
        />
      </ModalHeader>

      <ModalBody className="px-6">
        <span className="text-gray-900 font-bold text-2xl">{t("Review")}</span>
        <p className="text-sm text-gray-600 mb-3">
          {t(
            "Si vous n'êtes pas d'accord avec la catégorie détectée vous pouvez en sélectionner une autre. Notre équipe de conformité examinera la modification avant qu'elle ne soit approuvée.",
          )}
        </p>

        {requestType === "category" &&
          !impactLoading &&
          typeof impactCount === "number" &&
          impactCount > 1 && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
              {t("categoryPropagation.warningMultiImage", {
                imageCount: impactCount,
              })}
            </p>
          )}

        <div className="w-full mb-2 flex items-center justify-center gap-2">
          <span className="text-sm text-gray-700 whitespace-nowrap">
            {t("Show/hide segmentation")}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={showSegmentation}
            onClick={() => setShowSegmentation((prev) => !prev)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#2AB9B8] focus:ring-offset-2 ${
              showSegmentation ? "bg-[#2AB9B8]" : "bg-gray-200"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 shrink-0 rounded-full bg-white ring-0 transition ${
                showSegmentation ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        <div className="w-full mb-3 flex justify-center relative">
          {imageUrl && selectedAnomalie?.points_konva?.length > 0 && (
            <div
              className="w-full flex justify-center relative bg-[#f6f6f6] rounded-lg overflow-hidden"
              style={{ maxWidth: 550, height: 250 }}
            >
              <WasteToolbarThumbnail
                subregion={{
                  ...selectedAnomalie,
                  color:
                    formattedColorMaterial(
                      selectedAnomalie?.cls || selectedAnomalie?.oldCls || "",
                    ) || "#2AB9B8",
                }}
                image_url={imageUrl}
                maxWidth={550}
                maxHeight={350}
                fillRectangle
                showSegmentation={showSegmentation}
                strokeWidth={3}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col mb-3">
          <label className="text-gray-700 text-sm font-medium mb-1.5">
            {t("review_request_type")}
          </label>
          <div className="relative">
            <select
              id="requestType"
              name="requestType"
              value={requestType}
              onChange={(e) => setRequestType(e.target.value)}
              className="appearance-none bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-[#2AB9B8] focus:border-[#2AB9B8] block w-full px-3 py-2 pr-10 cursor-pointer h-10"
            >
              <option value="category">
                {t("review_request_type_change_category")}
              </option>
              <option value="delete">
                {t("review_request_type_delete_object")}
              </option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="flex flex-col mb-3">
          <label className="text-gray-700 text-sm font-medium mb-1.5">
            {t("Category")}
          </label>
          <div className="relative">
            <select
              id="category"
              name="category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled={requestType === "delete"}
              className={`appearance-none bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-[#2AB9B8] focus:border-[#2AB9B8] block w-full px-3 py-2 pr-10 cursor-pointer h-10 ${
                requestType === "delete" ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              <option value="" className="text-gray-400">
                {t("-- Select a category --")}
              </option>
              {wasteCategoriesByParent.map((group) => (
                <optgroup
                  key={group.parentCategory}
                  label={capitalizeFirstLetter(
                    getParentCategoryLabelPlural(t, group.parentCategory),
                  )}
                  style={{ color: "#9ca3af", fontWeight: 600 }}
                >
                  {group.items.map((item) => (
                    <option
                      key={item.referenceName}
                      value={item.referenceName}
                      style={{ color: "#222222" }}
                    >
                      {t(item.referenceName)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="flex flex-col mb-1">
          <label className="text-gray-700 text-sm font-medium mb-1.5">
            {t("Commentaire")}
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t("Leave your comment...")}
            className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-[#2AB9B8] focus:border-[#2AB9B8] resize-none"
            rows={4}
          ></textarea>
        </div>

        {propagationProgress && (
          <div className="mt-3">
            <p className="text-sm text-gray-600 mb-1">
              {t("categoryPropagation.applying")}
            </p>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#2AB9B8] transition-all duration-300"
                style={{
                  width: `${Math.min(100, Math.max(8, propagationProgress))}%`,
                }}
              />
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="flex justify-end gap-2 pt-4 pb-4 border-0 px-6">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={toggle}
            className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium"
          >
            {t("Cancel")}
          </button>
          {selectedAnomalie?.deletedAt &&
          typeof onRestore === "function" &&
          isAkanthasAccount &&
          viewMode === "admin" ? (
            <button
              type="button"
              disabled={isLoading || loading}
              onClick={async () => {
                setLoading(true);
                try {
                  await onRestore(selectedAnomalie.id);
                  toggle();
                } finally {
                  setLoading(false);
                }
              }}
              className="px-4 py-2 bg-[#2AB9B8] text-white rounded-full hover:bg-[#009493] disabled:opacity-50 text-sm font-medium"
            >
              {loading ? t("Restoring...") : t("Restore")}
            </button>
          ) : selectedAnomalie?.deletedAt ? null : (
            <button
              type="button"
              disabled={
                isLoading ||
                loading ||
                (requestType === "category" &&
                  (impactLoading || !selectedCategory?.trim()))
              }
              onClick={async () => {
                const polygonId = selectedAnomalie.id;

                if (requestType === "delete") {
                  setLoading(true);
                  if (isAkanthasAccount && viewMode === "admin") {
                    await handleDeleteSuggestion?.(polygonId, comment, {
                      accepted: true,
                      reviewRequestType: "delete",
                    });
                  } else {
                    await handleDeleteSuggestion?.(polygonId, comment, {
                      reviewRequestType: "delete",
                    });
                  }
                  setLoading(false);
                  toggle();
                  return;
                }

                // Impact count must be ready so the multi-image warning is known.
                if (impactLoading) return;
                if (!confirmPropagationIfNeeded()) return;

                setLoading(true);
                if (impactCount > 1) setPropagationProgress(12);
                let progressTimer = null;
                try {
                  if (impactCount > 1) {
                    progressTimer = setInterval(() => {
                      setPropagationProgress((prev) =>
                        prev == null || prev >= 90 ? prev : prev + 8,
                      );
                    }, 400);
                  }

                  if (isAkanthasAccount && viewMode === "admin") {
                    await handleObjectSegmentation(
                      polygonId,
                      comment,
                      selectedCategory,
                      "validation",
                      "category",
                      { propagate: true, sourceObject: selectedAnomalie },
                    );
                  } else {
                    await handleObjectSegmentation(
                      polygonId,
                      comment,
                      selectedCategory,
                      "client_review",
                      "category",
                      { propagate: true, sourceObject: selectedAnomalie },
                    );
                  }
                  setPropagationProgress(100);
                  toggle();
                } finally {
                  if (progressTimer) clearInterval(progressTimer);
                  setLoading(false);
                  setPropagationProgress(null);
                }
              }}
              className={`px-4 py-2 text-white rounded-full disabled:opacity-50 text-sm font-medium ${
                requestType === "delete"
                  ? "bg-[#dc2626] hover:bg-[#b91c1c]"
                  : "bg-[#2AB9B8] hover:bg-[#009493]"
              }`}
            >
              {requestType === "delete"
                ? t("Delete")
                : isAkanthasAccount && viewMode === "admin"
                  ? t("Validate")
                  : t("Send")}
            </button>
          )}
        </div>
      </ModalFooter>
      {loading && !propagationProgress && <LoadingScreen />}
    </Modal>
  );
};

export default WastePolygonModal;
